import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldAlert, Sparkles, Video, Image as ImageIcon, ArrowLeft, UploadCloud, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  IMAGE_MODELS,
  VIDEO_MODELS,
  estimateImageCostUSD,
  estimateVideoCostUSD,
  formatUSD,
  formatJPY,
  type ImageModelId,
} from "@/lib/modelPricing";

export const Route = createFileRoute("/_authenticated/admin_/lab")({
  head: () => ({
    meta: [
      { title: "AI Lab (Admin) | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLab,
});

interface ImageResult {
  url: string;
  model: string;
  costUSD: number;
  elapsedMs: number;
}
interface VideoResult {
  url?: string;
  modelId: string;
  durationSec: number;
  costUSD: number;
  status: "processing" | "completed" | "failed";
  externalJobId: string;
  error?: string;
}

function AdminLab() {
  const isAdminFn = useServerFn(getIsAdmin);
  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: s } = await supabase.auth.getSession();
        if (!s.session) {
          setIsAdmin(false);
          return;
        }
        const r = await isAdminFn({
          headers: { Authorization: `Bearer ${s.session.access_token}` },
        });
        setIsAdmin(r.isAdmin);
      } finally {
        setChecked(true);
      }
    })();
  }, [isAdminFn]);

  if (!checked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }
  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-destructive" />
            <h1 className="text-xl font-bold">アクセス権がありません</h1>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Sparkles className="h-7 w-7 text-primary" />
              AI Lab
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              全モデルを試して実コストを確認（クレジット消費なし／admin限定）
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              管理画面へ
            </Link>
          </Button>
        </div>

        <Tabs defaultValue="image" className="w-full">
          <TabsList>
            <TabsTrigger value="image">
              <ImageIcon className="mr-2 h-4 w-4" />
              画像生成
            </TabsTrigger>
            <TabsTrigger value="video">
              <Video className="mr-2 h-4 w-4" />
              動画生成
            </TabsTrigger>
          </TabsList>
          <TabsContent value="image" className="mt-6">
            <ImageLab />
          </TabsContent>
          <TabsContent value="video" className="mt-6">
            <VideoLab />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

/* ===================== 画像 ===================== */

function ImageLab() {
  const [model, setModel] = useState<ImageModelId>(IMAGE_MODELS[0].id);
  const [aspect, setAspect] = useState<"1:1" | "9:16" | "16:9">("1:1");
  const [prompt, setPrompt] = useState("");
  const [refImages, setRefImages] = useState<string[]>(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ImageResult[]>([]);

  const estimated = estimateImageCostUSD(model);

  async function uploadImageFile(file: File): Promise<string> {
    const { data: s } = await supabase.auth.getSession();
    const userId = s.session?.user.id;
    if (!userId) throw new Error("not signed in");
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${userId}/lab-input/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("uploads").upload(path, file);
    if (error) throw error;
    const { data: signed } = await supabase.storage.from("uploads").createSignedUrl(path, 60 * 60 * 6);
    return signed?.signedUrl ?? "";
  }

  async function handleGenerate() {
    if (!prompt.trim()) {
      toast.error("プロンプトを入力してください");
      return;
    }
    setLoading(true);
    try {
      const { data: s } = await supabase.auth.getSession();
      const inputs = refImages.filter((u) => !!u);
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-lab-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${s.session?.access_token}`,
          },
          body: JSON.stringify({
            model,
            prompt,
            aspect_ratio: aspect,
            input_image_urls: inputs.length > 0 ? inputs : undefined,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? "生成失敗");
        return;
      }
      setResults((p) => [
        {
          url: data.output_url,
          model: data.model,
          costUSD: data.cost_usd,
          elapsedMs: data.elapsed_ms,
        },
        ...p,
      ]);
      toast.success("生成完了");
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  }

  const refCount = refImages.filter((u) => !!u).length;

  return (
    <Card className="p-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label>モデル</Label>
            <Select value={model} onValueChange={(v) => setModel(v as ImageModelId)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IMAGE_MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label} — {formatUSD(m.costPerImage)}/枚
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {IMAGE_MODELS.find((m) => m.id === model)?.description}
            </p>
          </div>
          <div>
            <Label>アスペクト比</Label>
            <Select value={aspect} onValueChange={(v) => setAspect(v as typeof aspect)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1:1">正方形 1:1</SelectItem>
                <SelectItem value="9:16">縦長 9:16</SelectItem>
                <SelectItem value="16:9">横長 16:9</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">参照画像（image-to-image / 任意・最大3枚）</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {refImages.map((url, idx) => (
                <LabDropzone
                  key={idx}
                  label={`画像 ${idx + 1}`}
                  hint={idx === 0 ? "ベース画像" : "参照"}
                  accept="image/*"
                  kind="image"
                  value={url}
                  onClear={() =>
                    setRefImages((arr) => arr.map((v, i) => (i === idx ? "" : v)))
                  }
                  onUpload={async (f) => {
                    toast.info("アップロード中...");
                    try {
                      const u = await uploadImageFile(f);
                      setRefImages((arr) => arr.map((v, i) => (i === idx ? u : v)));
                      toast.success("画像アップ完了");
                    } catch (err) {
                      toast.error(String(err));
                    }
                  }}
                />
              ))}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              画像を入れると image-to-image / 編集モードになります（テキスト指示で改変・合成）
            </p>
          </div>
          <div>
            <Label>プロンプト{refCount > 0 ? "（編集指示）" : "（英語推奨）"}</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              placeholder={
                refCount > 0
                  ? "Make the sky purple, add a moon..."
                  : "A cinematic portrait of a Japanese woman..."
              }
              className="mt-1 resize-y"
            />
          </div>
        </div>
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="text-xs font-medium text-muted-foreground">推定コスト</p>
            <p className="mt-1 text-2xl font-bold">{formatUSD(estimated)}</p>
            <p className="text-sm text-muted-foreground">{formatJPY(estimated)}</p>
            {refCount > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                参照画像 {refCount} 枚 → image-to-image
              </p>
            )}
          </div>
          <Button onClick={handleGenerate} disabled={loading} className="w-full">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            生成
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">結果（新しい順）</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {results.map((r, i) => (
              <div key={i} className="overflow-hidden rounded-lg border bg-card">
                <img src={r.url} alt="" className="aspect-square w-full object-cover" />
                <div className="p-3 space-y-1">
                  <p className="text-xs font-mono truncate">{r.model}</p>
                  <p className="text-sm font-bold text-primary">
                    {formatUSD(r.costUSD)} / {formatJPY(r.costUSD)}
                  </p>
                  <p className="text-xs text-muted-foreground">{(r.elapsedMs / 1000).toFixed(1)}秒</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ===================== 動画 ===================== */

function VideoLab() {
  const [modelId, setModelId] = useState<string>(VIDEO_MODELS[0].id);
  const [duration, setDuration] = useState<number>(VIDEO_MODELS[0].durations[0]);
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<VideoResult[]>([]);
  const pollersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const model = VIDEO_MODELS.find((m) => m.id === modelId)!;
  const estimated = estimateVideoCostUSD(modelId, duration);

  useEffect(() => {
    if (!model.durations.includes(duration)) setDuration(model.durations[0]);
  }, [modelId, duration, model.durations]);

  // Storage アップロード（adminのフォルダ）
  async function uploadFile(file: File, kind: "image" | "video"): Promise<string> {
    const { data: s } = await supabase.auth.getSession();
    const userId = s.session?.user.id;
    if (!userId) throw new Error("not signed in");
    const ext = file.name.split(".").pop() ?? (kind === "image" ? "jpg" : "mp4");
    const path = `${userId}/lab-input/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("uploads").upload(path, file);
    if (error) throw error;
    const { data: signed } = await supabase.storage.from("uploads").createSignedUrl(path, 60 * 60 * 6);
    return signed?.signedUrl ?? "";
  }

  async function handleSubmit() {
    if (model.inputs.image && !imageUrl) return toast.error("画像URLを入力");
    if (model.inputs.video && !videoUrl) return toast.error("動画URLを入力");
    if (model.id === "heygen/talking-photo" && !prompt.trim()) return toast.error("テキスト必須");
    if (model.provider === "seedance" && !prompt.trim())
      return toast.error("Seedance はプロンプト必須（参照は [Image1] / [Video1] で指定）");

    setLoading(true);
    try {
      const { data: s } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-lab-video`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${s.session?.access_token}`,
          },
          body: JSON.stringify({
            action: "submit",
            model_id: modelId,
            input_image_url: imageUrl || undefined,
            input_video_url: videoUrl || undefined,
            prompt: prompt || undefined,
            duration,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? "送信失敗");
        return;
      }
      const result: VideoResult = {
        modelId,
        durationSec: duration,
        costUSD: estimated,
        status: "processing",
        externalJobId: data.external_job_id,
      };
      setResults((p) => [result, ...p]);
      if (data.poll_action) startPolling(data.external_job_id, data.poll_action, modelId);
      else toast.info(data.message ?? "送信しました");
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  }

  function startPolling(jobId: string, pollAction: string, mid: string) {
    if (pollersRef.current[jobId]) return;
    let attempts = 0;
    pollersRef.current[jobId] = setInterval(async () => {
      attempts++;
      if (attempts > 120) {
        clearInterval(pollersRef.current[jobId]);
        delete pollersRef.current[jobId];
        return;
      }
      try {
        const { data: s } = await supabase.auth.getSession();
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-lab-video`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${s.session?.access_token}`,
            },
            body: JSON.stringify({ action: pollAction, external_job_id: jobId, model_id: mid }),
          },
        );
        const data = await res.json();
        if (data.status === "completed") {
          clearInterval(pollersRef.current[jobId]);
          delete pollersRef.current[jobId];
          setResults((p) =>
            p.map((r) => (r.externalJobId === jobId ? { ...r, status: "completed", url: data.videoUrl } : r)),
          );
          toast.success("動画生成完了");
        } else if (data.status === "failed") {
          clearInterval(pollersRef.current[jobId]);
          delete pollersRef.current[jobId];
          setResults((p) =>
            p.map((r) => (r.externalJobId === jobId ? { ...r, status: "failed", error: data.error } : r)),
          );
          toast.error("生成失敗");
        }
      } catch {
        // ignore
      }
    }, 5000);
  }

  return (
    <Card className="p-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label>モデル</Label>
            <Select value={modelId} onValueChange={setModelId}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIDEO_MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">{model.description}</p>
          </div>
          <div>
            <Label>長さ（秒）</Label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {model.durations.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d}秒
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <LabDropzone
              label="画像 1"
              hint={model.inputs.image ? "人物画像など" : "このモデルでは未使用"}
              accept="image/*"
              kind="image"
              disabled={!model.inputs.image}
              value={imageUrl}
              onClear={() => setImageUrl("")}
              onUpload={async (f) => {
                toast.info("アップロード中...");
                try {
                  const url = await uploadFile(f, "image");
                  setImageUrl(url);
                  toast.success("画像アップ完了");
                } catch (err) {
                  toast.error(String(err));
                }
              }}
            />
            <LabDropzone
              label="動画 1"
              hint={model.inputs.video ? "ベース動画" : "このモデルでは未使用"}
              accept="video/*"
              kind="video"
              disabled={!model.inputs.video}
              value={videoUrl}
              onClear={() => setVideoUrl("")}
              onUpload={async (f) => {
                toast.info("アップロード中...");
                try {
                  const url = await uploadFile(f, "video");
                  setVideoUrl(url);
                  toast.success("動画アップ完了");
                } catch (err) {
                  toast.error(String(err));
                }
              }}
            />
          </div>
          {(model.inputs.image || model.inputs.video) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">URLで指定する</summary>
              <div className="mt-2 space-y-2">
                {model.inputs.image && (
                  <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="画像URL https://..." />
                )}
                {model.inputs.video && (
                  <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="動画URL https://..." />
                )}
              </div>
            </details>
          )}
          {model.inputs.text && (
            <div>
              <Label>{model.id === "heygen/talking-photo" ? "喋らせるテキスト" : "プロンプト（任意）"}</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
                placeholder={model.id === "heygen/talking-photo" ? "こんにちは、今日はいい天気ですね..." : "A cinematic shot of..."}
                className="mt-1 resize-y"
              />
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="text-xs font-medium text-muted-foreground">推定コスト</p>
            <p className="mt-1 text-2xl font-bold">{formatUSD(estimated)}</p>
            <p className="text-sm text-muted-foreground">{formatJPY(estimated)}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              {model.resolution} / {duration}秒
            </p>
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Video className="mr-2 h-4 w-4" />}
            生成（クレジット消費なし）
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">結果（新しい順）</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {results.map((r) => (
              <div key={r.externalJobId} className="overflow-hidden rounded-lg border bg-card p-3 space-y-2">
                <p className="text-xs font-mono truncate">{r.modelId}</p>
                <p className="text-sm font-bold text-primary">
                  {formatUSD(r.costUSD)} / {formatJPY(r.costUSD)} （{r.durationSec}秒）
                </p>
                {r.status === "processing" && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    生成中... (job: {r.externalJobId.slice(0, 8)})
                  </div>
                )}
                {r.status === "completed" && r.url && (
                  <video src={r.url} controls className="w-full rounded" />
                )}
                {r.status === "failed" && (
                  <p className="text-sm text-destructive">失敗: {r.error}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ===================== Lab Dropzone ===================== */

function LabDropzone({
  label,
  hint,
  accept,
  kind,
  value,
  onUpload,
  onClear,
  disabled = false,
}: {
  label: string;
  hint: string;
  accept: string;
  kind: "image" | "video";
  value: string;
  onUpload: (f: File) => void | Promise<void>;
  onClear: () => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  return (
    <div className={cn("space-y-1.5", disabled && "opacity-50")}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold">{label}</Label>
        <span className="text-[10px] text-muted-foreground">{hint}</span>
      </div>
      {value ? (
        <div className="relative overflow-hidden rounded-lg border bg-muted/30">
          {kind === "image" ? (
            <img src={value} alt={label} className="aspect-square w-full object-cover" />
          ) : (
            <video src={value} controls className="aspect-video w-full bg-black object-contain" />
          )}
          <button
            type="button"
            onClick={onClear}
            aria-label="削除"
            className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow hover:bg-destructive hover:text-destructive-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            if (disabled) return;
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            if (disabled) return;
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void onUpload(f);
          }}
          className={cn(
            "flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-4 text-center transition-colors",
            !disabled && "hover:border-primary hover:bg-primary/5",
            drag && !disabled && "border-primary bg-primary/5",
            disabled && "cursor-not-allowed",
          )}
        >
          {kind === "image" ? (
            <ImageIcon className="h-7 w-7 text-muted-foreground" />
          ) : (
            <Video className="h-7 w-7 text-muted-foreground" />
          )}
          <UploadCloud className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs font-medium">
            ドラッグ＆ドロップ
            <br />
            <span className="text-primary">または選択</span>
          </p>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
